<template>
  <div class="flex w-full relative bg-gray-50 dark:bg-navy-1000">
    <SiteCommandSidebar
      @select="selectSideBarOption"
    />
    <!-- ACTIVE SITE COMMAND PAGE -->
    <div class="flex flex-col w-full bg-gray-50 dark:bg-navy-1000">
      <!-- SITE COMMAND HEADER -->
      <div class="flex-col items-center flex">
        <div
          class="sectionHeader flex items-center justify-center w-full z-20 lg:z-15 lg:transition-all duration-200 transform"
          :class="headerScrolled"
        >
          <SiteCommandLogo class="transition-all duration-200" :class="logoClass" />
        </div>
      </div>
      <!-- SELECTED SITE COMMAND COMPONENT -->
      <div v-if="readyToLoad" class="site-command-page h-full" :class="pageClass">
        <keep-alive include="AssetGraphList">
          <component :is="currentViewComponent" />
        </keep-alive>
      </div>
      <div v-else class="w-full h-full">
        <LoadingSpinnerPage />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import { getControllerConfig, getControllerVersions, getSiteCommandFeatures } from "../services/SiteService"

import { defineAsyncComponent } from "vue"
// Async components (each becomes a separate chunk)
const OutputTracking = defineAsyncComponent({
  loader: () => import("../../Site-Command/components/Outputs/OutputTracking.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const OverrideTracking = defineAsyncComponent({
  loader: () => import("../../Site-Command/components/Overrides/OverrideTracking.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const DiscreteInputTracking = defineAsyncComponent({
  loader: () => import("../../Site-Command/components/DiscreteInputs/DiscreteInputTracking.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const SetupOptions = defineAsyncComponent({
  loader: () => import("../../Site-Command/components/SetupOptions/SetupOptions.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const EDLList = defineAsyncComponent({
  loader: () => import("./EDL/EDLList.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const CILList = defineAsyncComponent({
  loader: () => import("./CIL/CILList.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const UpsellsPage = defineAsyncComponent({
  loader: () => import("./Upsells/UpsellsPage.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const WashPackages = defineAsyncComponent({
  loader: () => import("../../Site-Command/components/WashPackages/WashPackages.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const EquipmentAsset = defineAsyncComponent({
  loader: () => import("./Assets/EquipmentAsset.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetGraphList = defineAsyncComponent({
  loader: () => import("./AssetGraph/AssetGraphList.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AssetGraphView = defineAsyncComponent({
  loader: () => import("./AssetGraph/AssetGraphView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const MaintenanceModeView = defineAsyncComponent({
  loader: () => import("./MaintenanceMode/MaintenanceModeView.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const CCMMI = defineAsyncComponent({
  loader: () => import("./CCMMI/CCMMI.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const NOEchannels = defineAsyncComponent({
  loader: () => import("./NOE/NOEchannels.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const WashMapConfig = defineAsyncComponent({
  loader: () => import("./WashMapConfig/WashMapConfig.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ConveyorDrive = defineAsyncComponent({
  loader: () => import("./ConveyorDrive/ConveyorDrive.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ControlWord = defineAsyncComponent({
  loader: () => import("./AssistanceTool/ControlWord/ControlWord.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const OmniControl = defineAsyncComponent({
  loader: () => import("./AssistanceTool/OmniControl/OmniControl.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const OmniVac = defineAsyncComponent({
  loader: () => import("./AssistanceTool/OmniVac/OmniVac.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const AirgateTables = defineAsyncComponent({
  loader: () => import("./AssistanceTool/AirgateTables/AirgateTables.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const MirrorBumps = defineAsyncComponent({
  loader: () => import("./AssistanceTool/MirrorBumps/MirrorBumps.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const BlowerFlips = defineAsyncComponent({
  loader: () => import("./AssistanceTool/BlowerFlips/BlowerFlips.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const PidAttack = defineAsyncComponent({
  loader: () => import("./AssistanceTool/PidAttack/PidAttack.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const DryRun = defineAsyncComponent({
  loader: () => import("./AssistanceTool/DryRun/DryRun.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const ConveyorSpeedSelector = defineAsyncComponent({
  loader: () => import("./AssistanceTool/ConveyorSpeedSelector/ConveyorSpeedSelector.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const RequestsLogTable = defineAsyncComponent({
  loader: () => import("./RequestsLogTable.vue"),
  loadingComponent: LoadingSpinnerPage,
})
const SiteCommandDashboard = defineAsyncComponent({
  loader: () => import("./Dashboard/SiteCommandDashboard.vue"),
  loadingComponent: LoadingSpinnerPage,
})
import LoadingSpinnerPage from "@/components/Loading/LoadingSpinner/LoadingSpinnerPage.vue"
import SiteCommandSidebar from '../../Site-Command/components/SiteCommandSidebar.vue'
import SiteCommandLogo from "@/assets/Cruz_Control_Site_Command_Logo_Full_Color.svg"

export default {
  name: "SiteCommand",
  components: {
    SiteCommandSidebar,
    SiteCommandLogo,
    LoadingSpinnerPage
  },
  data() {
    return {
      loadedSiteCommandControllerFeatures: false,
    }
  },
  computed: {
    ...mapState(["screen", "headers", "orientation"]),
    ...mapGetters([
      "showingModuleHeader",
      "atTopOfPage",
      "loadedGlobalValues",
      "getSiteCommandControllerFeatures",
      "getIsTenantSuperuser",
      "isPromptProceed",
      "getProceedAction",
    ]),
    currentViewComponent() {
      if (!this.readyToLoad) return null

      if (this.path === "Outputs") return OutputTracking
      if (this.$route.path.includes("overrides")) return OverrideTracking
      if (this.path === "Inputs") return DiscreteInputTracking
      if (this.path === "Setup Options") return SetupOptions
      if (this.path === "Equipment Dependency List" && this.getSiteCommandControllerFeatures.includes("EDL_CIL")) return EDLList
      if (this.path === "Critical Infrastructure List" && this.getSiteCommandControllerFeatures.includes("EDL_CIL")) return CILList
      if (this.$route.path.includes("upsells")) return UpsellsPage
      if (this.$route.path.includes("washes")) return WashPackages
      if (this.$route.path.includes("/equipment/")) return EquipmentAsset
      if (this.path === "Asset Graph") return AssetGraphList
      if (this.path === "Asset Graph View") return AssetGraphView
      if (this.path === "Maintenance Mode") return MaintenanceModeView
      if (this.$route.path.includes("ccmmi")) return CCMMI
      if (this.$route.path.includes("noe_channels")) return NOEchannels
      if (this.$route.path.includes("wash_map_config")) return WashMapConfig
      if (this.$route.path.includes("conveyor_drive")) return ConveyorDrive
      if (this.$route.path.includes("control_word") && (this.getSiteCommandControllerFeatures.includes("Control_Word_C5") || this.getSiteCommandControllerFeatures.includes("Control_Word_C4"))) return ControlWord
      if (this.$route.path.includes("omni_control") && this.getSiteCommandControllerFeatures.includes("Omni_Control")) return OmniControl
      if (this.$route.path.includes("omni_vac") && this.getSiteCommandControllerFeatures.includes("Omni_Vac")) return OmniVac
      if (this.$route.path.includes("airgate_tables") && this.getSiteCommandControllerFeatures.includes("Airgate_Table")) return AirgateTables
      if (this.$route.path.includes("blower_flips") && this.getSiteCommandControllerFeatures.includes("Blower_Flips")) return BlowerFlips
      if (this.$route.path.includes("pid_attack_module") && this.getSiteCommandControllerFeatures.includes("PID_Attack_Module")) return PidAttack
      if (this.$route.path.includes("mirror_bumps") && (this.getSiteCommandControllerFeatures.includes("Bumps_Mirror") || this.getSiteCommandControllerFeatures.includes("Bumps_Wrap_HB"))) return MirrorBumps
      if (this.$route.path.includes("dry_run") && this.getSiteCommandControllerFeatures.includes("Dry_Run")) return DryRun
      if (this.$route.path.includes("conveyor_speed_selector") && this.getSiteCommandControllerFeatures.includes("Conveyor_Speed_Config")) return ConveyorSpeedSelector
      if (this.path === "RequestLogs") return RequestsLogTable
      if (this.path === "Site Command") return SiteCommandDashboard

      // default
      return SiteCommandDashboard
    },
    needLoad() {
      // MARK IF PATH REQUIRES STATE VALUES PRELOAD
      return {
        "RequestLogs": [
          "preloadData",
        ],
        "Equipment Dependency List": [
          "getSiteCommandControllerFeatures",
        ],
        "Critical Infrastructure List": [
          "getSiteCommandControllerFeatures",
        ],
        "Assistance Tool": [
          "getSiteCommandControllerFeatures",
        ],
      }
    },
    readyToLoad() {
      if (!this.needLoad[this.path]) return true
      for (let needToLoad of this.needLoad[this.path]) {
        if (!this.loadedGlobalValues[needToLoad] && !this.loadedSiteCommandControllerFeatures) return false
      }
      return true
    },
    headerScrolled() {
      if (this.isMobileLandscape && !this.isTablet && !this.screen.xl) return "hidden"
      let className = "md:fixed lg:shadow-md bg-gray-50 dark:bg-navy-1000 lg:bg-white-off "
      if (this.atTopOfPage || !this.screen.lg) {
        className += "h-16 lg:h-24 "
      }
      else className += "shadow-lg h-24 lg:h-16 "
      if (!this.showingModuleHeader && this.screen.lg) className += "-translate-y-full md:translate-y-0 "
      else className += "-translate-y-0 "
      return className
    },
    pageClass() {
      let classes = "flex flex-col relative "
      if (!this.isMobileLandscape || (this.isTablet)) classes += "md:mt-16 lg:mt-24 "
      if (this.$route.path.includes("asset")) classes += "items-center "
      else classes += "items-start "
      return classes
    },
    path() {
      return this.$route.name
    },
    logoClass() {
      if (this.atTopOfPage && this.screen.lg) return "h-14 lg:h-20 py-2 my-0"
      else return "h-14 lg:h-16 py-2" 
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isTablet() {
      return /ipad|tablet/i.test(window.navigator.userAgent.toLowerCase())
    },
    isMobileLandscape() {
      return this.isMobile && this.orientation.landscape
    },
    isMobilePortrait() {
      return this.isMobile && this.orientation.portrait
    },
  },
  watch: {
    path(val) {
      this.setSTPage(val)
    },
    async isPromptProceed(val) {
      if (val === true && this.getProceedAction === "openInitializationModal") {
        await this.openInitializationModal()
        this.setPrompt([null, null])
      }
    },
  },
  mounted() {
    this.showModuleHeader(true)
    // this.loadSiteData()
    this.loadConfigData()
    this.loadConfigVersions()
    this.loadFeatures()
  },
  methods: {
    // ...mapActions(["setSTData"]),
    ...mapMutations([
      "setSTPage",
      "setPrompt",
      "showModuleHeader",
      "setSiteCommandControllerData",
      "setSiteCommandConfigVersionOptions",
      "setSiteCommandControllerFeatures",
      "setActiveModal"
    ]),
    // loadSiteData() {
    //   this.getPreloadData()
    // },
    selectSideBarOption(option) {
      this.setSTPage(option)
    },
    // async getPreloadData() {
    //   await getSiteTrackPreload(this.headers)
    //   .then((response) => {
    //     if (response?.status === 200) this.setSTData(response.data)
    //     else {
    //       console.log(response)
    //       if (response.status === 403) {
    //         this.setPrompt(["You do not have the required permissions to access this data.", "closePrompt", null, { header: "Error" }])
    //       }
    //       else this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
    //     }
    //   }, (error) => {
    //     console.log(error)
    //     this.setPrompt(["Could not load all data for this site!", "getPreloadData", null, { header: "Error" }])
    //   })
    // },
    async loadConfigData() {
      if (!this.getIsTenantSuperuser) return
      await getControllerConfig(this.headers)
      .then((response) => {
        if (response?.status === 200) {
          this.setSiteCommandControllerData([response.data?.controller_config, false])
          if (response.data?.controller_config?.config_data?.ip_address == null || response.data?.controller_config?.config_data?.version == null) {
            this.setPrompt([
              "Your Site Command has not been initialized yet. Would you like to set things up?",
              "openInitializationModal", null,
              { header: "Hold on" }
            ])
          }
        }
        else {
          console.log(response)
          this.setSiteCommandControllerData([{}, true])
          this.setPrompt(["Could not load controller configuration data for this site!", "openInitializationModal", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setSiteCommandControllerData([{}, true])
        this.setPrompt(["Could not load controller configuration data for this site!", "openInitializationModal", null, { header: "Error" }])
      })
    },
    async loadConfigVersions() {
      if (!this.getIsTenantSuperuser) return
      await getControllerVersions(this.headers)
      .then((response) => {
        if (response?.status === 200) {
          this.setSiteCommandConfigVersionOptions(response.data?.results)
        }
        else {
          console.log(response)
          this.setSiteCommandConfigVersionOptions([])
        }
      }, (error) => {
        console.log(error)
        this.setSiteCommandConfigVersionOptions([])
      })
    },
    async loadFeatures() {
      await getSiteCommandFeatures(this.headers)
      .then((response) => {
        console.log(response)
        if (response?.status === 200) {
          this.setSiteCommandControllerFeatures(response.data?.results)
          if (this.$route.name === "Assistance Tool") {
            if (this.$route.params.subPage === "control_word") {
              if (!this.getSiteCommandControllerFeatures?.includes("Control_Word_C5")
                && !this.getSiteCommandControllerFeatures?.includes("Control_Word_C4")
                && !this.getSiteCommandControllerFeatures.includes("Control_Word_Standard_Offering")
                && !this.getSiteCommandControllerFeatures?.includes("Requeue_Control_Word")
              ) {
                this.setPrompt(["This site does not have access to Additional Features.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            else if (this.$route.params.subPage === "mirror_bumps") {
              if (!this.getSiteCommandControllerFeatures?.includes("Bumps_Mirror") && !this.getSiteCommandControllerFeatures?.includes("Bumps_Wrap_HB")) {
                this.setPrompt(["This site does not have access to Mirror Bumps.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            else if (this.$route.params.subPage === "omni_control") {
              if (!this.getSiteCommandControllerFeatures?.includes("Omni_Control")) {
                this.setPrompt(["This site does not have access to Omni Control.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            // else if (this.$route.params.subPage === "omni_vac") {
            //   if (!this.getSiteCommandControllerFeatures?.includes("Omni_Vac")) {
            //     this.setPrompt(["This site does not have access to Omni/Vacuum PID.", "closePrompt", null, { header: "Error" }])
            //     setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
            //   }
            // }
            else if (this.$route.params.subPage === "airgate_tables") {
              if (!this.getSiteCommandControllerFeatures?.includes("Airgate_Table")) {
                this.setPrompt(["This site does not have access to Blower Flip & Airgate.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            else if (this.$route.params.subPage === "dry_run") {
              if (!this.getSiteCommandControllerFeatures?.includes("Dry_Run")) {
                this.setPrompt(["This site does not have access to Dry Run.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            else if (this.$route.params.subPage === "conveyor_speed_selector") {
              if (!this.getSiteCommandControllerFeatures?.includes("Conveyor_Speed_Config")) {
                this.setPrompt(["This site does not have access to Conveyor Speed Selector.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            if (this.$route.params.subPage === "blower_flips") {
              if (!this.getSiteCommandControllerFeatures?.includes("Blower_Flips")) {
                this.setPrompt(["This site does not have access to Blower Flips.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
            if (this.$route.params.subPage === "pid_attack_module") {
              if (!this.getSiteCommandControllerFeatures?.includes("PID_Attack_Module")) {
                this.setPrompt(["This site does not have access to the PID Attack Module.", "closePrompt", null, { header: "Error" }])
                setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
              }
            }
          }
          if (this.$route.name === "Equipment Dependency List" && !this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) {
            this.setPrompt(["This site does not have access to the Equipment Dependency List.", "closePrompt", null, { header: "Error" }])
            setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
          }
          if (this.$route.name === "Critical Infrastructure List" && !this.getSiteCommandControllerFeatures?.includes("EDL_CIL")) {
            this.setPrompt(["This site does not have access to the Critical Infrastructure List.", "closePrompt", null, { header: "Error" }])
            setTimeout(() => this.$router.push({ name: 'RequestLogs' }), 10)
          }
        }
        else {
          console.log(response)
          this.setSiteCommandControllerFeatures([])
          if (response.status === 503) {
            this.setPrompt(["Unable to reach the controller. Site Command features could not be loaded.", "closePrompt", null, { header: "Error" }])
          }
          else this.setPrompt(["Could not load all features for this site!", "closePrompt", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setSiteCommandControllerFeatures([])
        this.setPrompt(["Could not load all features for this site!", "closePrompt", null, { header: "Error" }])
      })
      this.loadedSiteCommandControllerFeatures = true
    },
    openInitializationModal() {
      if (!this.getIsTenantSuperuser) return
      this.setActiveModal({
        component: "SiteCommandInitializeModal"
      })
    }
  },
};
</script>